import { useEffect, useState } from "react";
import { X, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";

const TAG_FIELDS = [
  { key: "sectors", label: "Sectors", max: 2 },
  { key: "keywords", label: "Keywords", max: 6 },
];
const TEXT_FIELDS = [
  { key: "relevance_definition", label: "What 'relevant' means" },
  { key: "importance_context", label: "Why this matters commercially" },
];

const norm = (s) => String(s || "").trim().toLowerCase();

export default function AutofillModal({ open, onClose, options, current, onApply }) {
  const opts = options || {};
  const cur = current || {};

  const [included, setIncluded] = useState({});
  const [selected, setSelected] = useState({ sectors: [], keywords: [] });
  const [removedExisting, setRemovedExisting] = useState({ sectors: [], keywords: [] });
  const [texts, setTexts] = useState({ relevance_definition: "", importance_context: "" });
  const [custom, setCustom] = useState({ sectors: "", keywords: "" });

  useEffect(() => {
    if (!open) return;
    setIncluded({
      sectors: false,
      keywords: false,
      relevance_definition: !!opts.relevance_definition,
      importance_context: !!opts.importance_context,
    });
    setSelected({ sectors: [], keywords: [] });
    setRemovedExisting({ sectors: [], keywords: [] });
    setTexts({
      relevance_definition: opts.relevance_definition || "",
      importance_context: opts.importance_context || "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const existingByField = {
    sectors: (cur.sectors || []).filter(Boolean),
    keywords: (cur.keywords || []).filter(Boolean),
  };

  const retainedExisting = (field) =>
    (existingByField[field] || []).filter(
      (value) => !(removedExisting[field] || []).some((removed) => norm(removed) === norm(value)),
    );

  if (!open) return null;

  const tagFieldByKey = Object.fromEntries(TAG_FIELDS.map((f) => [f.key, f]));
  const toggleInclude = (key) => {
    const willEnable = !included[key];
    if (willEnable && tagFieldByKey[key]) {
      const sel = selected[key] || [];
      if (sel.length === 0) {
        toast.error(`Select at least one ${tagFieldByKey[key].label.toLowerCase()} to include ${tagFieldByKey[key].label}.`);
        return;
      }
    }
    if (!willEnable && tagFieldByKey[key]) {
      // Unchecking should clear all selected chips for that field
      setSelected((s) => ({ ...s, [key]: [] }));
    }
    setIncluded((s) => ({ ...s, [key]: !s[key] }));
  };

  const toggleChip = (field, max, value) => {
    const existing = retainedExisting(field);
    const sel = selected[field] || [];
    const isSel = sel.some((x) => norm(x) === norm(value));
    if (isSel) {
      const nextSelection = sel.filter((x) => norm(x) !== norm(value));
      setSelected((s) => ({ ...s, [field]: nextSelection }));
      if (nextSelection.length === 0) {
        setIncluded((s) => ({ ...s, [field]: false }));
      }
      return;
    }
    if (existing.some((x) => norm(x) === norm(value))) {
      toast.message("Already added to this field.");
      return;
    }
    if (existing.length >= max) {
      toast.error(`${field === "sectors" ? "Sectors" : "Keywords"} is already full (${max}). Remove one on the form first.`);
      return;
    }
    if (existing.length + sel.length >= max) {
      toast.error(`You can select up to ${max - existing.length} more here.`);
      return;
    }
    setSelected((s) => ({ ...s, [field]: [...sel, value] }));
    setIncluded((s) => ({ ...s, [field]: true }));
  };

  const toggleExisting = (field, value) => {
    setRemovedExisting((state) => {
      const removed = state[field] || [];
      const isRemoved = removed.some((item) => norm(item) === norm(value));
      return {
        ...state,
        [field]: isRemoved
          ? removed.filter((item) => norm(item) !== norm(value))
          : [...removed, value],
      };
    });
  };

  const addCustom = (field, max) => {
    const val = (custom[field] || "").trim();
    if (!val) return;
    setCustom((c) => ({ ...c, [field]: "" }));
    toggleChip(field, max, val);
  };

  const apply = () => {
    // Guard: any checked tag field must have at least one chip selected
    for (const { key, label } of TAG_FIELDS) {
      if (included[key] && (selected[key] || []).length === 0) {
        toast.error(`Select at least one ${label.toLowerCase()} before applying. Uncheck ${label} or pick a chip.`);
        return;
      }
    }
    const payload = {};
    TAG_FIELDS.forEach(({ key, max }) => {
      if (!included[key]) return;
      const existing = retainedExisting(key);
      const merged = [...existing];
      (selected[key] || []).forEach((v) => {
        if (merged.length < max && !merged.some((x) => norm(x) === norm(v))) merged.push(v);
      });
      payload[key] = merged;
    });
    TEXT_FIELDS.forEach(({ key }) => {
      if (!included[key]) return;
      payload[key] = texts[key];
    });
    if (Object.keys(payload).length === 0) {
      toast.error("Select at least one field to apply.");
      return;
    }
    onApply(payload);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="autofill-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-[#E2E8F0]">
          <div>
            <div className="inline-flex items-center gap-2 text-[#0F1F3D] font-bold text-lg">
              <Sparkles className="h-4 w-4 text-[#2563EB]" /> AI suggestions
            </div>
            <p className="text-sm text-[#64748B] mt-1">
              Select the fields and suggestions you want to fill. Text fields are editable.
            </p>
          </div>
          <button onClick={onClose} data-testid="autofill-close" className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-[#F1F5F9]">
            <X className="h-5 w-5 text-[#64748B]" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {TAG_FIELDS.map(({ key, label, max }) => {
            const options_ = opts[key] || [];
            if (options_.length === 0) return null;
            const existing = retainedExisting(key);
            const sel = selected[key] || [];
            const full = existing.length >= max;
            const remaining = Math.max(0, max - existing.length - sel.length);
            return (
              <div key={key} data-testid={`autofill-field-${key}`}>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={!!included[key]}
                    onChange={() => toggleInclude(key)}
                    data-testid={`autofill-include-${key}`}
                    className="h-4 w-4 accent-[#0F1F3D]"
                  />
                  <span className="text-sm font-semibold text-[#0F1F3D]">{label}</span>
                  <span className="text-xs text-[#94A3B8]">
                    {existing.length}/{max} set · {included[key] ? `${remaining} more selectable` : "skipped"}
                  </span>
                </label>

                <div className={`mt-2.5 ${full ? "opacity-70" : ""}`}>
                    {existing.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {existing.map((t, i) => (
                          <button
                            key={`ex-${i}`}
                            type="button"
                            onClick={() => toggleExisting(key, t)}
                            data-testid={`autofill-existing-chip-${key}-${i}`}
                            title={`Remove ${t}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#F1F5F9] text-xs text-[#475569] hover:bg-[#FEE2E2] hover:text-[#B91C1C]"
                          >
                            <Check className="h-3 w-3 text-[#10B981]" /> {t} <span className="ml-0.5 opacity-70">×</span>
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {options_.map((opt, i) => {
                        const isSel = sel.some((x) => norm(x) === norm(opt));
                        const alreadyHave = existing.some((x) => norm(x) === norm(opt));
                        return (
                          <button
                            key={`opt-${i}`}
                            type="button"
                            disabled={alreadyHave}
                            onClick={() => toggleChip(key, max, opt)}
                            data-testid={`autofill-chip-${key}-${i}`}
                            data-selected={isSel ? "true" : "false"}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                              alreadyHave
                                ? "bg-[#F8FAFC] border-[#E2E8F0] text-[#CBD5E1] cursor-not-allowed"
                                : isSel
                                ? "bg-[#0F1F3D] border-[#0F1F3D] text-white"
                                : "bg-white border-[#CBD5E1] text-[#334155] hover:border-[#0F1F3D] hover:text-[#0F1F3D]"
                            }`}
                          >
                            {isSel && <Check className="h-3 w-3" />}
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                    {/* Custom selected values (typed, not in AI options) */}
                    {sel.filter((v) => !options_.some((o) => norm(o) === norm(v))).length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {sel.filter((v) => !options_.some((o) => norm(o) === norm(v))).map((v, i) => (
                          <button
                            key={`custom-${i}`}
                            type="button"
                            onClick={() => toggleChip(key, max, v)}
                            data-testid={`autofill-custom-chip-${key}-${i}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border bg-[#0F1F3D] border-[#0F1F3D] text-white"
                          >
                            <Check className="h-3 w-3" /> {v} <span className="ml-0.5 opacity-70">×</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {/* Type-your-own */}
                    {!full && (
                      <div className="flex gap-2 mt-2.5">
                        <input
                          value={custom[key]}
                          onChange={(e) => setCustom((c) => ({ ...c, [key]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(key, max); } }}
                          placeholder={`Type your own ${label.toLowerCase()}…`}
                          data-testid={`autofill-custom-input-${key}`}
                          className="flex-1 text-xs px-3 py-2 rounded-md border border-[#CBD5E1] focus:outline-none focus:ring-2 focus:ring-[#0F1F3D]/20"
                        />
                        <button
                          type="button"
                          onClick={() => addCustom(key, max)}
                          data-testid={`autofill-custom-add-${key}`}
                          className="px-3 py-2 rounded-md border border-[#CBD5E1] text-xs font-semibold text-[#0F1F3D] hover:bg-[#F1F5F9]"
                        >
                          Add
                        </button>
                      </div>
                    )}
                    {full && (
                      <p className="mt-2 text-xs text-[#B45309]" data-testid={`autofill-full-${key}`}>
                        {label} is already full ({max}). Remove one on the form to add more.
                      </p>
                    )}
                </div>
              </div>
            );
          })}

          {TEXT_FIELDS.map(({ key, label }) => {
            if (!opts[key]) return null;
            return (
              <div key={key} data-testid={`autofill-field-${key}`}>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={!!included[key]}
                    onChange={() => toggleInclude(key)}
                    data-testid={`autofill-include-${key}`}
                    className="h-4 w-4 accent-[#0F1F3D]"
                  />
                  <span className="text-sm font-semibold text-[#0F1F3D]">{label}</span>
                </label>
                {included[key] && (
                  <textarea
                    rows={3}
                    value={texts[key]}
                    onChange={(e) => setTexts((s) => ({ ...s, [key]: e.target.value }))}
                    data-testid={`autofill-text-${key}`}
                    className="mt-2 w-full px-3 py-2.5 rounded-md border border-[#E2E8F0] text-sm focus:outline-none focus:ring-2 focus:ring-[#0F1F3D]"
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        {(() => {
          const hasInvalidTagCheck = TAG_FIELDS.some(({ key }) => included[key] && (selected[key] || []).length === 0);
          return (
            <div className="px-6 py-4 border-t border-[#E2E8F0] flex items-center justify-end gap-2">
              <button onClick={onClose} data-testid="autofill-cancel" className="px-4 py-2 rounded-md border border-[#E2E8F0] text-sm font-semibold text-[#475569] hover:bg-[#F1F5F9]">
                Cancel
              </button>
              <button
                onClick={apply}
                disabled={hasInvalidTagCheck}
                title={hasInvalidTagCheck ? "Select at least one sector or keyword for each checked field" : undefined}
                data-testid="autofill-apply"
                className={`inline-flex items-center gap-2 px-5 py-2 rounded-md text-sm font-semibold ${hasInvalidTagCheck ? "bg-[#CBD5E1] text-white cursor-not-allowed" : "bg-[#0F1F3D] text-white hover:bg-[#172A4F]"}`}
              >
                <Check className="h-4 w-4" /> Apply selected
              </button>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
